import { useEffect, useMemo, useRef, useState } from "react";
import { X, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";

// Location brand colours — same hues as the patient-lookup Location chips.
export const PROVIDER_LOCATION_COLOR: Record<string, string> = {
  Iowa: "#5B7C99",
  Minnesota: "#6A5ACD",
  Colorado: "#1b6e4a",
};

/**
 * Autocomplete for associating provider NAMES with footage. Suggestions pull
 * from every location library (providers.listActive) and are colour-coded by
 * location; picks become removable chips. Selection is restricted to library
 * entries (no free-text) so names stay canonical for the rename cascade.
 *
 * Presentation-only: the caller supplies a label / reveal affordance.
 */
export function ProviderPicker({
  value,
  onChange,
  placeholder = "Type a provider name…",
  compact = false,
  autoFocus = false,
  disabled = false,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  compact?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const [input, setInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load every Active provider once and filter locally, so matches appear on
  // every keystroke instead of waiting on a server round-trip.
  const { data: roster = [] } = trpc.providers.listActive.useQuery(undefined, {
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
  const results = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (!q) return [];
    return roster.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 10);
  }, [roster, input]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const addProvider = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (!value.some((v) => v.toLowerCase() === trimmed.toLowerCase())) {
      onChange([...value, trimmed]);
    }
    setInput("");
    setShowDropdown(false);
    setHighlight(-1);
    inputRef.current?.focus();
  };

  const removeProvider = (idx: number) => onChange(value.filter((_, i) => i !== idx));

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, -1));
    } else if (e.key === "Enter") {
      if (highlight >= 0 && results[highlight]) {
        e.preventDefault();
        addProvider(results[highlight].name);
      }
    } else if (e.key === "Backspace" && !input && value.length > 0) {
      e.preventDefault();
      removeProvider(value.length - 1);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  return (
    <div className={cn(compact ? "space-y-1" : "space-y-1.5")}>
      <div className="relative">
        <Input
          ref={inputRef}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setShowDropdown(true);
            setHighlight(-1);
          }}
          onFocus={() => input.trim() && setShowDropdown(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className={cn("text-sm", compact ? "h-8" : "h-9")}
          autoComplete="off"
          autoFocus={autoFocus}
          disabled={disabled}
        />
        {showDropdown && results.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover text-popover-foreground border border-border rounded-lg shadow-md overflow-hidden max-h-64 overflow-y-auto"
          >
            {results.map((p, idx) => (
              <button
                key={p.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  addProvider(p.name);
                }}
                className={cn(
                  "w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 hover:bg-muted transition-colors",
                  idx === highlight && "bg-muted",
                )}
              >
                {/* Headshot — ringed in the provider's location colour. Falls
                    back to a placeholder icon when no photo is set. */}
                <span
                  className="w-7 h-7 rounded-full overflow-hidden bg-muted border-2 flex items-center justify-center shrink-0"
                  style={{ borderColor: PROVIDER_LOCATION_COLOR[p.location] ?? "#888" }}
                >
                  {p.photoUrl ? (
                    <img src={p.photoUrl} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-3.5 h-3.5 text-muted-foreground" />
                  )}
                </span>
                <span className="font-medium text-foreground flex-1">{p.name}</span>
                <span
                  className="text-[11px] font-medium shrink-0"
                  style={{ color: PROVIDER_LOCATION_COLOR[p.location] ?? undefined }}
                >
                  {p.location}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((name, idx) => (
            <span
              key={`${name}-${idx}`}
              className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium"
            >
              <span className="font-medium">{name}</span>
              <button
                type="button"
                onClick={() => removeProvider(idx)}
                className="text-primary/70 hover:text-destructive p-0.5 rounded"
                title="Remove provider"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
